import React from 'react';
import { SectionSummary } from '../utils/types';
import { T } from '../designTokens';

interface PartialSummaryNoticeProps {
  sections: SectionSummary[];
  /**
   * Ignore skipped sections entirely and report only sections that errored.
   * For a view that already has a more specific notice covering *why* other
   * sections didn't run (LockedReportView's gpcOnly note names the single
   * section that did) — that note says nothing about a stage that failed
   * outright, which this flag still needs to surface (#1168 review).
   */
  onlyErrors?: boolean;
}

/**
 * Disclosure for the redacted sales/shared summary views (SalesReportPage,
 * LockedReportView) when the scan behind this summary didn't fully score
 * every section — either a full scan where a stage errored partway through,
 * or a lite scan (mode: 'gpc', 'optout', ...) that never ran some sections.
 * Since #1785 every free www scan is the full audit, so the lite lane is the
 * fallback (the /tools checkers, and anonymous audits past the daily cap,
 * #1778) rather than the normal free result.
 * calculateScoreBreakdown (backend utils/helpers.ts) excludes both `skipped`
 * and `error` sections from the denominator identically, so either one
 * shrinks the scoreable surface the same way — a single-section pass or a
 * three-quarters-failed scan can both produce a high, unqualified
 * overallScore next to "N/A" section cards (post-#1167, those read N/A
 * rather than a false 0%, which makes the unqualified score beside them more
 * conspicuous, not less).
 *
 * ReportHero already discloses this for the full scan view: detectScoreCap
 * feeds ScoreExplainer's cap messaging, and a GPC-only scan gets its own
 * verdict badge instead of the aggregate score. Neither reaches this
 * summary — the page a prospect opens from a cold email — which had no
 * equivalent (#1168). PartialScanBanner is a separate, ScanResultsPage-only
 * component and does not cover these two views either.
 */
export default function PartialSummaryNotice({ sections, onlyErrors = false }: PartialSummaryNoticeProps) {
  const notRun = sections.filter((s) => s.status === 'skipped' || s.status === 'error');
  const relevant = onlyErrors ? notRun.filter((s) => s.status === 'error') : notRun;
  if (relevant.length === 0) return null;

  // #2064: a skipped section hidden from the clause still did not run.
  const ranCount = sections.length - notRun.length;
  const skippedNames = relevant.filter((s) => s.status === 'skipped').map((s) => s.displayName);
  const erroredNames = relevant.filter((s) => s.status === 'error').map((s) => s.displayName);

  const clauses: string[] = [];
  if (skippedNames.length > 0) clauses.push(`${skippedNames.join(', ')} not tested`);
  if (erroredNames.length > 0) clauses.push(`${erroredNames.join(', ')} failed to complete`);

  return (
    <div
      role="note"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '12px 16px',
        borderRadius: 8,
        backgroundColor: T.yellow50,
        border: `1px solid ${T.warningBorder}`,
        color: T.amber800,
        fontSize: 13,
        lineHeight: 1.5,
        marginBottom: 24,
      }}
    >
      <span aria-hidden="true" style={{ flexShrink: 0 }}>⚠️</span>
      <span>
        <strong>Partial scan:</strong> only {ranCount} of {sections.length} audit area
        {sections.length === 1 ? '' : 's'} ran ({clauses.join('; ')}). Scores reflect completed
        sections only.
      </span>
    </div>
  );
}
